/**
 * Customer statement (C22, D03 §2 flow 7): "كشف الحساب" opens the history dialog directly, as the old
 * `fetchHistory("debt", id, name, "account")` did (App.tsx:22734-23037 → `HistoryModal` account variant), over
 * `GET /customers/:id/statement` — every operation on the account's IQD and USD lines, ids only (D-017, R-22 fixed),
 * running balances from the ledger (D-013, R-21 fixed), office dates (D-064). "مشاركة PDF" (old
 * `handleShareAccountStatement` → the PDF dialog) prints the same A4 statement (`autoPrint`); [C35] PDF = the server's PDF of the same statement (D-146), with the closing balance in words (D-148); was the browser's
 * save-as-PDF until the B5 worker (as C19 / C20).
 */
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { HistoryModal, type HistoryFilter, type HistoryRow } from "@faylasuf/ui";
import { PRINT_ROOT_CLASS, StatementA4, customerSettlement, printDocument, type StatementA4Props } from "@faylasuf/print";
import { useDocuments, usePrintSettings } from "../../app/documents.js";
import type { CustomerAccountRow, CustomerStatementQuery, CustomerStatementRow } from "../../api/types.js";
import { useApp, useSession } from "../../app/context.js";
import { isSessionLost } from "../../app/errors.js";
import { keys } from "../../app/query.js";
import { officeDate } from "../wallets/StatementContainer.js";
import { decSub, fmt, fmtAbs, isZero } from "./format.js";
import { EntryVoucherContainer } from "./EntryVoucherContainer.js";
import { usePrintOffice } from "../../app/printOffice.js";

/** The dialog's type filter → operation types (as the box statement, C19). */
const TYPE_GROUPS: Readonly<Record<string, readonly string[]>> = {
  deposit: ["customer_deposit", "customer_opening_balance", "quick_deposit"],
  withdraw: ["customer_withdraw", "quick_withdraw"],
  transfer: ["customer_transfer", "transfer_out", "transfer_in", "transfer_out_settled", "transfer_in_settled", "transfer_currency_passthrough", "partner_charge"],
  manual_work: ["manual_work"],
  trade: ["usd_buy", "usd_sell", "currency_buy", "currency_sell", "currency_sell_for_usd", "account_conversion"],
  commission: [],
};

/** "In" = the office receives value from the customer: a trust grows or a debt shrinks. */
function isIn(kind: "debt" | "trust", amount: string): boolean {
  const neg = amount.startsWith("-");
  return kind === "trust" ? !neg : neg;
}

function hourOf(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat("en-GB", { timeZone, hour: "2-digit", hour12: false }).format(new Date(iso)).slice(0, 2);
}

export function CustomerStatementContainer({ account, usdHidden, autoPrint = false, onClose }: { account: CustomerAccountRow; usdHidden: boolean; autoPrint?: boolean; onClose: () => void }) {
  const { source, t, locale, now } = useApp();
  const { session, sessionLost } = useSession();
  const printOffice = usePrintOffice(); // [C29] F-286: identity lines in the A4 header
  const tz = session.office.timeZone;
  const [filter, setFilter] = useState<HistoryFilter>({ type: "", from: "", to: "", operator: "", hour: "" });
  const [printing, setPrinting] = useState(false);
  const [voucherFor, setVoucherFor] = useState<string | null>(null); // [C28] p02
  const docs = useDocuments();
  const printSettings = usePrintSettings();
  const [words, setWords] = useState("");
  const today = officeDate(now(), tz);

  const q: CustomerStatementQuery = {
    ...(filter.from ? { from: filter.from } : {}),
    ...(filter.to ? { to: filter.to } : {}),
    limit: 2000,
  };
  const allQuery = useQuery({ queryKey: keys.customerStatement(account.id, "all"), queryFn: () => source.customerStatement(account.id, { limit: 2000 }) });
  const query = useQuery({ queryKey: keys.customerStatement(account.id, q), queryFn: () => source.customerStatement(account.id, q) });
  if ((allQuery.error && isSessionLost(allQuery.error)) || (query.error && isSessionLost(query.error))) sessionLost();

  const unit = t("unit.iqd");
  const part = (iqd: string, usd: string): string => {
    const parts: string[] = [];
    if (!isZero(iqd)) parts.push(`${fmtAbs(iqd)} ${unit}`);
    if (!isZero(usd) && !usdHidden) parts.push(`${fmtAbs(usd)} $`);
    return parts.length ? parts.join(" / ") : "-";
  };

  const rows: HistoryRow[] = useMemo(() => {
    const list: readonly CustomerStatementRow[] = query.data?.rows ?? [];
    const group = filter.type && filter.type !== "incoming" && filter.type !== "outgoing" ? (TYPE_GROUPS[filter.type] ?? null) : null;
    return list
      .filter((r) => {
        if (usdHidden && isZero(r.iqd)) return false; // D-066: USD-only rows hidden with the USD columns
        if (group && !group.includes(r.type)) return false;
        const inbound = isIn(account.kind, isZero(r.iqd) ? r.usd : r.iqd);
        if (filter.type === "incoming" && !inbound) return false;
        if (filter.type === "outgoing" && inbound) return false;
        if (filter.hour && hourOf(r.postedAt, tz) !== filter.hour) return false;
        return true;
      })
      .map((r): HistoryRow => {
        const inIqd = !isZero(r.iqd) && isIn(account.kind, r.iqd) ? r.iqd : "0";
        const outIqd = !isZero(r.iqd) && !isIn(account.kind, r.iqd) ? r.iqd : "0";
        const inUsd = !isZero(r.usd) && isIn(account.kind, r.usd) ? r.usd : "0";
        const outUsd = !isZero(r.usd) && !isIn(account.kind, r.usd) ? r.usd : "0";
        const typeLabel = t(`optype.${r.type}`);
        return {
          id: r.operationId,
          dateLabel: r.officeDate,
          timeLabel: new Date(r.postedAt).toLocaleTimeString("en-US", { hour12: false, hour: "2-digit", minute: "2-digit", timeZone: tz }),
          typeKey: r.type,
          typeLabel: typeLabel === `optype.${r.type}` ? r.type : typeLabel,
          badgeTone: account.kind === "debt" ? "rose" : "emerald",
          description: r.counterparties.join(" / ") || "-",
          note: r.note ?? "-",
          operator: r.actorUsername ?? "",
          inLabel: part(inIqd, inUsd),
          outLabel: part(outIqd, outUsd),
          commissionLabel: "-",
          balanceLabel: `${fmt(r.runningIqd)} ${unit}`,
          balanceUsdLabel: usdHidden ? "" : fmt(r.runningUsd),
        };
      });
  }, [query.data, filter, account.kind, t, tz, unit, usdHidden]);

  useEffect(() => {
    if (autoPrint && query.data) setPrinting(true);
  }, [autoPrint, query.data]);

  useEffect(() => {
    if (!printing) return;
    const done = () => {
      setPrinting(false);
      if (autoPrint) onClose();
    };
    window.addEventListener("afterprint", done, { once: true });
    const h = setTimeout(() => printDocument("professional-print-template"), 80);
    return () => {
      clearTimeout(h);
      window.removeEventListener("afterprint", done);
    };
  }, [printing, autoPrint, onClose]);

  const totals = query.data?.totals;
  // [C36] D09 T-13: the old structured settlement box (ProfessionalPrint.tsx:1408-1545) on the server's figures —
  // totals of the listed movements, the balance before the last listed one, the closing balance and its direction
  // (D-148); the words of the closing balance in one phrase when both currencies are on the same side (D-326).
  // Old semantics (App.tsx:23135-23165): the closing figure is the latest listed movement's running balance (a period
  // filter closes at its end), "before" the running balance under it — the movement's own change taken off when it is
  // the only one listed.
  // [C36 review] the box follows the printed rows: with USD hidden (D-066) the USD-only rows are not printed, so "last"
  // and "before" come from the IQD rows; an empty period closes on the balance at its end (the latest movement of
  // the whole account up to the period's last day), never on today's balance.
  const printable = (query.data?.rows ?? []).filter((r) => !(usdHidden && isZero(r.iqd)));
  const upToEnd = (allQuery.data?.rows ?? []).filter((r) => (!filter.to || r.officeDate <= filter.to) && !(usdHidden && isZero(r.iqd)));
  const last = printable[0];
  const atEnd = last ?? upToEnd[0];
  const afterIqd = atEnd?.runningIqd ?? (filter.to ? "0" : (totals?.closingIqd ?? "0"));
  const afterUsd = atEnd?.runningUsd ?? (filter.to ? "0" : (totals?.closingUsd ?? "0"));
  const listed = printable;
  const settle = totals
    ? customerSettlement({
        kind: account.kind,
        totals: { ...totals, closingIqd: afterIqd, closingUsd: afterUsd },
        beforeIqd: listed[1]?.runningIqd ?? (last ? decSub(last.runningIqd, last.iqd) : afterIqd),
        beforeUsd: listed[1]?.runningUsd ?? (last ? decSub(last.runningUsd, last.usd) : afterUsd),
        fmt,
        units: { iqd: unit, usd: "$" },
        usdHidden,
      })
    : null;
  const wordsKey = JSON.stringify(settle?.words ?? null);
  useEffect(() => {
    if (!settle) return;
    if (settle.words.length === 0) {
      setWords("");
      return;
    }
    void docs.words(settle.words.map((w) => ({ lines: w.lines.map((l) => ({ ...l })), side: w.side }))).then((w) => setWords(w.filter((x) => x !== "").join(" — ")));
  }, [wordsKey]);
  const summary: StatementA4Props["summary"] = [];

  // [C35] one set of props for the browser print and the server PDF (D-146); words of the closing balances (D-148)
  const statementProps: StatementA4Props = {
    locale,
    title: t("history.account_title", { title: account.name }),
    customerName: account.name,
    dateLabel: today,
    docNumber: account.accountNumber,
    office: printOffice,
    ...(filter.from ? { periodFrom: filter.from } : {}),
    ...(filter.to ? { periodTo: filter.to } : {}),
    columns: [
      { header: t("date"), key: "date" },
      { header: t("history.category"), key: "category" },
      { header: t("description"), key: "description" },
      { header: t("note"), key: "note" },
      // [C29] the old A4 statement's headers "له / وارد" / "عليه / صادر" (App.tsx:23123-23124)
      { header: t("cust.dual_in"), key: "incoming" },
      { header: t("cust.dual_out"), key: "outgoing" },
      { header: t("history.balance"), key: "balance" },
    ],
    // [C29] F-287: the template splits a balance cell on "/" into the IQD and USD lines (old "<n> IQD / <n> $", App.tsx:23112)
    rows: rows.map((r) => ({ date: `${r.dateLabel} ${r.timeLabel}`, category: r.typeLabel, description: r.description, note: r.note, incoming: r.inLabel, outgoing: r.outLabel, balance: usdHidden ? r.balanceLabel : `${r.balanceLabel} / ${r.balanceUsdLabel} $` })),
    summary,
    settlement: settle?.settlement ?? null,
    hideProfit: !printSettings.showProfit, // [C36 review] D-337: no commission line on a hidden-profit statement
    remainingBalance: { amount: fmt(afterIqd), currency: unit }, // [C36 review] the same closing figure as the settlement box
    wordsLabel: words || null,
  };
  const exportPdf = () =>
    void docs.downloadPdf({ template: "statement_a4", props: statementProps as unknown as Record<string, unknown>, fileName: `statement-${account.accountNumber ?? account.id.slice(0, 8)}`, docType: "customer_statement", accountId: account.id });

  return (
    <>
      {!autoPrint && (
        <HistoryModal
          variant="account"
          title={account.name}
          rows={rows}
          filter={filter}
          onFilterChange={(patch) => setFilter((f) => ({ ...f, ...patch }))}
          onRowDetails={() => undefined}
          onVoucher={(id) => setVoucherFor(id)}
          onPrint={() => setPrinting(true)}
          onExportPdf={exportPdf}
          onClose={onClose}
          loading={query.isLoading}
          emptyState={(allQuery.data?.rows.length ?? 0) === 0 ? "no_movements" : "no_match"}
        />
      )}
      {voucherFor && <EntryVoucherContainer account={account} operationId={voucherFor} onClose={() => setVoucherFor(null)} />}
      {printing &&
        createPortal(
          <div className={PRINT_ROOT_CLASS} data-testid="print-root">
            <StatementA4 {...statementProps} />
          </div>,
          document.body,
        )}
    </>
  );
}
